import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  subscribeToRoom,
  leaveRoom,
  joinRoom
} from '../services/roomService';
import { startGame } from '../services/gameSelectionService';
import {
  sendFriendRequest,
  areFriends,
  hasPendingRequest,
  cancelFriendRequest
} from '../services/friendService';
import { subscribeToUserPresence, updateCurrentRoom } from '../services/presenceService';
import { formatRoomCode } from '../utils/codeGenerator';
import GameSelection from './GameSelection';
import TicTacToe from '../games/tictactoe/TicTacToe';
import Connect4 from '../games/connect4/Connect4';
import RockPaperScissors from '../games/rps/RockPaperScissors';
import DotsAndBoxes from '../games/dotsandboxes/DotsAndBoxes';
import Checkers from '../games/checkers/Checkers';
import Battleship from '../games/battleship/Battleship';
import Chess from '../games/chess/Chess';
import '../styles/RoomLobby.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const RoomLobby = ({ user }) => {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFriend, setIsFriend] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const [hostPresence, setHostPresence] = useState({ online: false });
  const [guestPresence, setGuestPresence] = useState({ online: false });

  useEffect(() => {
    // Save the room code to localStorage for re-entry
    localStorage.setItem('lastRoomCode', roomCode);

    let hasReceivedData = false;

    // Set a timeout to catch cases where subscription never fires
    const loadingTimeout = setTimeout(() => {
      if (!hasReceivedData) {
        console.error('Room loading timeout - no data received for room:', roomCode);
        setError('Failed to load room. The room may no longer exist.');
        setLoading(false);
        localStorage.removeItem('lastRoomCode');
        setTimeout(() => navigate('/profile'), 2000);
      }
    }, 3000); // 3 second timeout

    // Subscribe to room updates
    console.log('Setting up room subscription for:', roomCode);
    const unsubscribe = subscribeToRoom(roomCode, (data) => {
      console.log('Room subscription callback received data:', data ? 'exists' : 'null');
      hasReceivedData = true;
      clearTimeout(loadingTimeout);

      if (data) {
        // Validate room data structure
        if (!data.host || !data.host.userId) {
          console.error('Invalid room data - missing host information');
          setError('Room data is corrupted. Please create a new room.');
          setLoading(false);
          localStorage.removeItem('lastRoomCode');
          setTimeout(() => navigate('/profile'), 2000);
          return;
        }

        const wasGuest = roomData && roomData.guest && roomData.guest.userId === user.uid;
        const nowKicked = wasGuest && (!data.guest || data.guest.userId !== user.uid);

        if (nowKicked) {
          // Guest was kicked - clear saved room and redirect
          localStorage.removeItem('lastRoomCode');
          setError('You have been removed from the room');
          setLoading(false);
          setTimeout(() => navigate('/profile'), 2000);
          return;
        }

        setRoomData(data);
        setIsHost(data.host.userId === user.uid);
        setLoading(false);

        // Auto-rejoin logic: if user is not the host and room has no guest, rejoin
        const userIsHost = data.host.userId === user.uid;
        if (!userIsHost && !data.guest) {
          // User should be a guest but isn't in the room data - try to rejoin
          console.log('Detected missing guest data, attempting to rejoin...');
          joinRoom(roomCode, user.uid, user.displayName, user.photoURL)
            .then(() => {
              console.log('Successfully rejoined room');
            })
            .catch((err) => {
              console.error('Failed to auto-rejoin:', err);
              // If rejoin fails, we'll just show the waiting state
            });
        }
      } else {
        // Room was deleted - clear saved room
        localStorage.removeItem('lastRoomCode');
        setError('This room no longer exists');
        setLoading(false);
        setTimeout(() => navigate('/profile'), 2000);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, [roomCode, user.uid, user.displayName, user.photoURL, navigate]);

  // Check friendship status when guest joins
  useEffect(() => {
    const checkFriendshipStatus = async () => {
      if (!roomData?.guest) {
        setIsFriend(false);
        setRequestPending(false);
        return;
      }

      const otherUserId = isHost ? roomData.guest.userId : roomData.host.userId;

      try {
        // Check if already friends
        const friendStatus = await areFriends(user.uid, otherUserId);
        setIsFriend(friendStatus);

        // Only check pending requests if not already friends
        if (!friendStatus) {
          try {
            const pendingStatus = await hasPendingRequest(user.uid, otherUserId);
            setRequestPending(pendingStatus);
          } catch (pendingErr) {
            // Permission denied or other error - just assume no pending request
            console.warn('Could not check pending request status:', pendingErr.message);
            setRequestPending(false);
          }
        } else {
          setRequestPending(false);
        }
      } catch (err) {
        console.error('Error checking friendship status:', err);
        setIsFriend(false);
        setRequestPending(false);
      }
    };

    checkFriendshipStatus();
  }, [roomData, user.uid, isHost]);

  // Subscribe to host presence
  useEffect(() => {
    if (!roomData?.host) return;

    const unsubscribe = subscribeToUserPresence(roomData.host.userId, (presence) => {
      setHostPresence(presence);
    });

    return () => unsubscribe();
  }, [roomData?.host?.userId]);

  // Subscribe to guest presence
  useEffect(() => {
    if (!roomData?.guest) {
      setGuestPresence({ online: false });
      return;
    }

    const unsubscribe = subscribeToUserPresence(roomData.guest.userId, (presence) => {
      setGuestPresence(presence);
    });

    return () => unsubscribe();
  }, [roomData?.guest?.userId]);

  // Update current room in presence when entering/leaving
  useEffect(() => {
    // Set current room when component mounts
    updateCurrentRoom(user.uid, roomCode);

    // Clear current room when component unmounts
    return () => {
      updateCurrentRoom(user.uid, null);
    };
  }, [roomCode, user.uid]);

  const handleLeaveRoom = async () => {
    try {
      await leaveRoom(roomCode, user.uid);
      // Don't clear the saved room code when leaving, so they can re-enter
      navigate('/profile');
    } catch (err) {
      console.error('Error leaving room:', err);
    }
  };


  const handleAddFriend = async (isResend = false) => {
    if (!roomData?.guest) return;

    const otherPlayer = isHost ? roomData.guest : roomData.host;

    try {
      await sendFriendRequest(
        user.uid,
        user.displayName,
        user.photoURL,
        otherPlayer.userId,
        otherPlayer.displayName,
        isResend // Allow resending if this is a resend action
      );

      setRequestPending(true);
      alert(`Friend request ${isResend ? 're-sent' : 'sent'} to ${safeString(otherPlayer?.displayName, 'player')}!`);
    } catch (err) {
      console.error('Error sending friend request:', err);
      alert(err.message || 'Failed to send friend request');
    }
  };

  const handleCancelFriendRequest = async () => {
    if (!roomData?.guest) return;

    const otherPlayer = isHost ? roomData.guest : roomData.host;

    try {
      await cancelFriendRequest(user.uid, otherPlayer.userId);
      setRequestPending(false);
    } catch (err) {
      console.error('Error canceling friend request:', err);
      alert(err.message || 'Failed to cancel friend request');
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStartGame = async () => {
    // Either player can start the game once both agree
    try {
      await startGame(roomCode);
      // The UI will automatically update when the game status changes
    } catch (err) {
      console.error('Error starting game:', err);
      alert(err.message || 'Failed to start game');
    }
  };

  if (loading) {
    return (
      <div className="room-lobby-container">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading room...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="room-lobby-container">
        <div className="error-state">
          <p>{error}</p>
          <p>Redirecting...</p>
        </div>
      </div>
    );
  }

  if (!roomData) {
    return null;
  }

  const gameAgreed = roomData.gameSelected;

  // Show game if status is 'playing'
  if (roomData.status === 'playing') {
    // Render the appropriate game based on gameSelected
    if (roomData.gameSelected === 'tictactoe') {
      return <TicTacToe roomCode={roomCode} roomData={roomData} user={user} />;
    } else if (roomData.gameSelected === 'connect4') {
      return <Connect4 roomCode={roomCode} roomData={roomData} user={user} />;
    } else if (roomData.gameSelected === 'rps') {
      return <RockPaperScissors roomCode={roomCode} roomData={roomData} user={user} />;
    } else if (roomData.gameSelected === 'dotsandboxes') {
      return <DotsAndBoxes roomCode={roomCode} roomData={roomData} user={user} />;
    } else if (roomData.gameSelected === 'americancheckers') {
      return <Checkers roomCode={roomCode} roomData={roomData} user={user} variant="american" />;
    } else if (roomData.gameSelected === 'russiancheckers') {
      return <Checkers roomCode={roomCode} roomData={roomData} user={user} variant="russian" />;
    } else if (roomData.gameSelected === 'checkers') {
      // Legacy support for old "checkers" - default to American
      return <Checkers roomCode={roomCode} roomData={roomData} user={user} variant="american" />;
    } else if (roomData.gameSelected === 'battleship') {
      return <Battleship roomCode={roomCode} roomData={roomData} user={user} />;
    } else if (roomData.gameSelected === 'chess') {
      return <Chess roomCode={roomCode} roomData={roomData} user={user} />;
    } else {
      return (
        <div className="room-lobby-container">
          <div className="error-state">
            <p>Game type "{safeString(roomData.gameSelected, 'unknown')}" is not implemented yet</p>
            <button onClick={handleLeaveRoom}>Back to Lobby</button>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="room-lobby-container">
      <div className="room-lobby-card">
        {/* Logo/Brand */}
        <div className="lobby-brand" onClick={() => navigate('/profile')}>
          <h1 className="lobby-logo">RageBaitStudio</h1>
        </div>

        {/* Header */}
        <div className="lobby-header">
          <h2>Game Lobby</h2>
          <div className="room-code-display">
            <span className="code-label">Room Code:</span>
            <span className="code-value">{formatRoomCode(roomCode)}</span>
            <button className="copy-button" onClick={handleCopyCode}>
              {copied ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>
        </div>

        {/* Players Section */}
        <div className="players-section">
          <h3>Players</h3>

          {/* Host */}
          <div className="player-card">
            <div className="player-avatar-container">
              <img
                src={safeString(roomData.host?.photoURL, '')}
                alt={safeString(roomData.host?.displayName, 'Host')}
                className="player-avatar"
              />
              <div className={`presence-indicator ${hostPresence.online && hostPresence.currentRoom === roomCode ? 'active' : 'inactive'}`}></div>
            </div>
            <div className="player-info">
              <p className="player-name">
                {safeString(roomData.host?.displayName, 'Host')}
                {isHost && <span className="you-badge">You</span>}
                <span className="host-badge">Host</span>
              </p>
            </div>
          </div>

          {/* Guest */}
          {roomData.guest ? (
            <div className="player-card">
              <div className="player-avatar-container">
                <img
                  src={safeString(roomData.guest?.photoURL, '')}
                  alt={safeString(roomData.guest?.displayName, 'Guest')}
                  className="player-avatar"
                />
                <div className={`presence-indicator ${guestPresence.online && guestPresence.currentRoom === roomCode ? 'active' : 'inactive'}`}></div>
              </div>
              <div className="player-info">
                <p className="player-name">
                  {safeString(roomData.guest?.displayName, 'Guest')}
                  {!isHost && <span className="you-badge">You</span>}
                </p>
              </div>
              <div className="player-actions">
                {!isFriend && !requestPending && (
                  <button className="add-friend-button" onClick={handleAddFriend}>
                    Add Friend
                  </button>
                )}
                {requestPending && (
                  <>
                    <button className="resend-request-button" onClick={() => handleAddFriend(true)}>
                      Re-send
                    </button>
                    <button className="cancel-request-button" onClick={handleCancelFriendRequest}>
                      Cancel
                    </button>
                  </>
                )}
                {isFriend && (
                  <button className="already-friends-button" disabled>
                    Friends ✓
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="player-card waiting">
              <div className="waiting-indicator">
                <div className="waiting-spinner"></div>
              </div>
              <div className="player-info">
                <p className="player-name">Waiting for player...</p>
                <p className="player-status">
                  Share the room code with your friend!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Game Selection - Show immediately when there's a guest */}
        {roomData.guest && (
          <GameSelection roomCode={roomCode} user={user} roomData={roomData} />
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          {gameAgreed && (
            <button
              className="start-game-button"
              onClick={handleStartGame}
            >
              🎮 Start Game
            </button>
          )}

          <button className="leave-button" onClick={handleLeaveRoom}>
            Leave Room
          </button>
        </div>

        {/* Info Section */}
        {!roomData.guest && (
          <div className="info-section">
            <h4>Waiting for a player to join</h4>
            <p>Share the room code above with your friend to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomLobby;
